export const dateLabel = value => value ? new Date(value).toLocaleDateString('en-GB', {timeZone:'UTC', day:'2-digit', month:'short', year:'numeric'}) : '—';
export const exportColumns = [
    ['learner','Learner'],['traineeId','HRD trainee ID'],['moodleUserId','Moodle user ID'],
    ['source','Moodle source'],['provider','CPD provider'],['course','Course'],['moodleCourseId','Moodle course ID'],
    ['gender','Gender'],['profession','Profession'],['region','Region'],['zone','Zone'],['woreda','Woreda'],['facility','Facility'],
    ['license','License number'],['phone','Phone'],['email','Email'],['completedAt','Completed (UTC)'],
    ['issuedAt','Certificate issued (UTC)'],['receivedAt','Received (UTC)'],['score','Score (%)'],
    ['certificate','Certificate reference'],['verificationUrl','Verification URL'],
    ['eventId','Event ID'],['receiptId','Receipt ID'],
];
export async function exportExcel(rows, filterText) {
    const [{default:ExcelJS}, {saveAs}] = await Promise.all([import('exceljs'), import('file-saver')]);
    const book=new ExcelJS.Workbook();
    book.creator='HRD';
    const info=book.addWorksheet('Report details');
    info.addRows([['HRD e-learning completions'],['Generated (UTC)',new Date().toISOString()],['Filters',filterText],['Records',rows.length],['Note','Matched completions are recorded automatically; no approval is required.']]);
    info.getColumn(1).width=24; info.getColumn(2).width=100;
    const sheet=book.addWorksheet('Completions',{views:[{state:'frozen',ySplit:1}]});
    sheet.columns=exportColumns.map(([key,header]) => ({key,header,width:['learner','course','verificationUrl'].includes(key) ? 36 : 24}));
    rows.forEach(row => {
        const entry={...row};
        ['completedAt','issuedAt','receivedAt'].forEach(key => {entry[key]=row[key] ? new Date(row[key]) : null;});
        // ExcelJS writes supplied strings as text, including strings starting with =, +, - or @.
        sheet.addRow(entry);
    });
    sheet.getRow(1).font={bold:true,color:{argb:'FFFFFFFF'}};
    sheet.getRow(1).fill={type:'pattern',pattern:'solid',fgColor:{argb:'FF0F766E'}};
    sheet.autoFilter={from:{row:1,column:1},to:{row:rows.length+1,column:exportColumns.length}};
    ['completedAt','issuedAt','receivedAt'].forEach(key => {sheet.getColumn(key).numFmt='dd mmm yyyy hh:mm';});
    saveAs(new Blob([await book.xlsx.writeBuffer()],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),'hrd-elearning-completions.xlsx');
}
export async function exportPdf(rows, filterText) {
    const {jsPDF}=await import('jspdf');
    const doc=new jsPDF({orientation:'landscape',unit:'mm',format:'a3'});
    const response=await fetch(`${process.env.BASE_URL}fonts/NotoSansEthiopic-Regular.ttf`);
    if (!response.ok) throw new Error('PDF font could not be loaded. Please retry.');
    const bytes=new Uint8Array(await response.arrayBuffer());
    let binary='';
    for(let offset=0;offset<bytes.length;offset+=8192) binary+=String.fromCharCode(...bytes.subarray(offset,offset+8192));
    doc.addFileToVFS('NotoSansEthiopic-Regular.ttf',btoa(binary));
    doc.addFont('NotoSansEthiopic-Regular.ttf','NotoEthiopic','normal');
    // Noto's script font contains Ethiopic glyphs only. Render mixed text as
    // separate font runs so English headers and Amharic names both remain searchable.
    const runs=value=>String(value).match(/[\u1200-\u139f\u2d80-\u2ddf\uab00-\uab2f]+|[^\u1200-\u139f\u2d80-\u2ddf\uab00-\uab2f]+/g) || [];
    const useFont=run=>doc.setFont(/[\u1200-\u139f\u2d80-\u2ddf\uab00-\uab2f]/.test(run)?'NotoEthiopic':'helvetica','normal');
    const textWidth=value=>runs(value).reduce((width,run)=>{useFont(run);return width+doc.getTextWidth(run);},0);
    const wrap=(value,maxWidth)=>{
        const lines=[];let line='';
        for(const character of String(value)){
            if(character==='\n'){lines.push(line);line='';continue;}
            if(line && textWidth(line+character)>maxWidth){
                const split=line.lastIndexOf(' ');
                if(split>line.length/2){lines.push(line.slice(0,split));line=line.slice(split+1)+character;}
                else {lines.push(line);line=character;}
            }else line+=character;
        }
        if(line || !lines.length)lines.push(line);
        return lines;
    };
    const text=(value,x,y)=>{
        const lines=Array.isArray(value)?value:[value];
        const lineHeight=doc.getFontSize()/doc.internal.scaleFactor*1.15;
        lines.forEach((line,index)=>{let left=x;runs(line).forEach(run=>{useFont(run);doc.text(run,left,y+index*lineHeight);left+=doc.getTextWidth(run);});});
    };
    const widths=[44,34,83,22,31,30,27,24,17,76];
    const keys=['learner','source','course','gender','profession','region','facility','completedAt','score','certificate'];
    const labels=['Learner','Moodle source','Course','Gender','Profession','Region','Facility','Completed (UTC)','Score %','Certificate'];
    let y=0;
    const header=() => {
        doc.setTextColor(15,58,64); doc.setFontSize(18); text('HRD | E-learning completions',12,15);
        doc.setFontSize(8); text(`Generated ${new Date().toISOString()}  •  ${rows.length} records  •  Matched completions are recorded automatically`,12,22);
        const lines=wrap(filterText,390); text(lines,12,28); y=32+lines.length*3.5;
        doc.setFillColor(15,118,110); doc.rect(12,y,388,10,'F'); doc.setTextColor(255); doc.setFontSize(8);
        let x=12; labels.forEach((label,i)=>{text(label,x+2,y+6); x+=widths[i];}); y+=12;
    };
    header();
    rows.forEach((row,index)=>{
        doc.setFontSize(8);
        const cells=keys.map((key,i)=>wrap(String(key === 'completedAt' ? dateLabel(row[key]) : row[key] ?? '—'),widths[i]-4));
        const detail=`HRD ID: ${row.traineeId || 'Unmatched'} | Moodle user: ${row.moodleUserId} | Provider: ${row.provider} | Zone: ${row.zone} | Woreda: ${row.woreda} | License: ${row.license || '—'} | Phone: ${row.phone || '—'} | Email: ${row.email || '—'} | Issued: ${dateLabel(row.issuedAt)} | Received: ${dateLabel(row.receivedAt)} | Event: ${row.eventId} | Receipt: ${row.receiptId}${row.verificationUrl ? ' | Verify: '+row.verificationUrl : ''}`;
        doc.setFontSize(7);
        const details=wrap(detail,384);
        const cellHeight=Math.max(...cells.map(cell=>cell.length))*3.8+5;
        const height=cellHeight+details.length*3.2+5;
        if (y+height>282) {doc.addPage();header();}
        if (index%2===0) {doc.setFillColor(243,248,248);doc.rect(12,y,388,height,'F');}
        doc.setFontSize(8);doc.setTextColor(31,51,65);
        let x=12;cells.forEach((cell,i)=>{text(cell,x+2,y+4);x+=widths[i];});
        doc.setFontSize(7);doc.setTextColor(90,106,119);text(details,14,y+cellHeight+2);y+=height;
    });
    const pages=doc.getNumberOfPages();
    for(let page=1;page<=pages;page++){doc.setPage(page);doc.setFontSize(8);doc.setTextColor(100);text(`Page ${page} of ${pages}`,380,290);}
    doc.save('hrd-elearning-completions.pdf');
}
